/* eslint-disable max-lines */
// libs
import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { log } from 'shared/helpers/newRelic';
import get from 'lodash/get';

// global components
import SomethingWentWrong from 'shared/components/SomethingWentWrong';
import Toast, { ToastProps } from 'shared/ui-components/Toast';
import NFLoader from 'shared/ui-components/NFLoader';

// contexts
import CartContext from './context';

// api
import {
  addToCart,
  applyCoupon as applyCouponApi,
  deleteCartItem,
  getCartRefresh,
  removeCoupon as removeCouponApi,
  replaceCartItemSize as replaceCartItemSizeApi,
  toggleRewardToCart,
  updateCartItems as updateCartItemsApi,
  setShippingAddressV2 as setShippingAddressApiV2,
} from 'shared/api/cart';
import {
  setGuestShippingAddress as setGuestShippingAddressApi,
  setShippingAddress as setShippingAddressApi,
} from 'shared/api/address';

// helpers
import {
  getAddress,
  getAddressErrorMessage,
  getCartAppliedOffersDetails,
  getCartErrorMessage,
  getCartInfoMessage,
  getCartItemCounts,
  getCartItemQuantity,
  getCouponCode,
  getCouponDiscount,
  getFomoTag,
  getFreeItems,
  getGrandTotal,
  getOOSItems,
  getPaymentDetails,
  getProducts,
  getRewardsDTO,
  getShippingCommunicationText,
  getShippingMessage,
  isAddressContinueEnabled,
  isCartContinueEnabled,
  shouldPincodeToCartRefresh,
  updateCartDataOnDatalayer,
  getConvenienceChargesData,
  getFreeGiftsBannerData,
  getFreeSamples,
  getOOSFreeItems,
} from './helpers';
import { getDeviceType, DEVICE_TYPE } from 'shared/helpers/app';
import NFA from 'shared/helpers/analytics/NFAnalytics';
import { toXDecimal } from 'shared/layouts/Coupons/helper';
import { setPincodeToStorage } from 'shared/layouts/DetailsPage/components/ProductInfo/storage';

// constants
import { BASE_CART_URL } from 'shared/constants/uri';
import { TAG_IDENTIFIER } from 'shared/helpers/SentryLogger/constants';
import { DEVICE_TYPE as DEVICE } from 'shared/helpers/analytics/constants';

// defs
import {
  ApiItem,
  ApiSizeItem,
  Cart,
  HandleRemoveItemProps,
  RemoveCartItemProps,
} from 'shared/types/cart';
import { AddressDataApi } from 'shared/types/address';
import { hideCart } from 'shared/helpers/messaging';

const TEXTS = {
  REWARD_SUCCESS: 'Reward added to cart.',
  REMOVE_SUCCESS: 'Reward removed from cart.',
};

interface CartProviderProps {
  children: React.ReactNode;
}

function CartProvider({ children }: CartProviderProps) {
  const [isFetching, setIsFetching] = useState(true);
  const [errorStatusCode, setErrorStatusCode] = useState('');
  const [cart, setCart] = useState<Cart | undefined>();
  const [undoInfo, setUndoInfo] = useState<{ productId: string; quantity: number }[]>([]);
  const [showLoader, setShowLoader] = useState(false);
  const [cartApiMessage, setCartApiMessage] = useState('');
  const [couponSuccessMessage, setCouponSuccessMessage] = useState('');
  const [customToastProps, setCustomToastProps] = useState<ToastProps>({ message: '' });
  const { pathname } = useLocation();
  const [showCouponAppliedNudge, setShowCouponAppliedNudge] = useState<boolean>(false);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [showRemoveConfirmOOS, setShowRemoveConfirm] = useState(false);
  const [removeOOSFreeSamples, setRemoveOOSFreeSamples] = useState(false);

  // for future currency handling
  const [currencySymbol] = useState('₹');

  // boolean value which decide whether we should send pincode in the api payload
  const shouldSendPincode = useMemo(() => shouldPincodeToCartRefresh(pathname), [pathname]);

  const onCartChange = (cartData: Cart | undefined) => {
    setCart(cartData);
    updateCartDataOnDatalayer(cartData);
  };

  const handleCartRefresh = useCallback(() => {
    let isValid = true;
    const sourcePlatform = getDeviceType() === DEVICE_TYPE.DESKTOP ? DEVICE.DESKTOP : DEVICE.MWEB;

    NFA.setSourcePlatform(sourcePlatform);
    setIsFetching(true);
    getCartRefresh({ shouldSendPincode })
      .then(async (data) => {
        if (isValid === false) {
          return;
        }

        setCart(data.cart);
        setIsFetching(false);
      })
      .catch((err) => {
        if (isValid === false) {
          return;
        }
        setErrorStatusCode(err?.statusCode);
      });

    return () => {
      isValid = false;
    };
  }, [shouldSendPincode]);

  useEffect(handleCartRefresh, [handleCartRefresh]);

  /** Hide toast message on path change */
  useEffect(() => {
    setCartApiMessage('');
    setUndoInfo([]);

    const baseCartUrl = `${BASE_CART_URL}/`;

    // check to not hide coupon success message if page is cart
    // as coupon is applied on coupon page and then redirected to Cart page
    if (pathname !== baseCartUrl) {
      setCouponSuccessMessage('');
    }
  }, [pathname]);

  const addItemsToCart = useCallback(
    (products: string[]) => {
      const items = products.map((productId) => ({ productId, quantity: 1 }));

      setShowLoader(true);
      return addToCart({ items })
        .then(() => {
          return getCartRefresh({ shouldSendPincode });
        })
        .then(({ cart: newCart }) => {
          setShowLoader(false);
          onCartChange(newCart);
          setCartApiMessage('Item added to cart.');
          return {
            success: true,
          };
        })
        .catch(({ message }) => {
          setShowLoader(false);
          setCartApiMessage(message);

          return {
            message,
            success: false,
          };
        });
    },
    [shouldSendPincode]
  );

  const removeCartItems = useCallback(({ items }: RemoveCartItemProps) => {
    const cartItemIdProducts = items?.filter((item) => item.cartItemId);

    const nonCartItemProducts = items?.filter((item) => !item.cartItemId);

    const productIdDTO = cartItemIdProducts?.map((item) => ({
      productId: item.productId,
      cartItemId: item.cartItemId,
    }));

    const productIds = nonCartItemProducts?.map((item) => item.productId);

    // we can undo only items which are not OOS
    const newUndoInfo = items
      ?.filter((item) => item.isOutOfStock === false)
      .map(({ productId, quantity, productCustomAttributes }) => ({
        productId,
        quantity,
        productCustomAttributes,
      }));

    setShowLoader(true);

    return deleteCartItem(productIds, productIdDTO)
      .then(({ cart: newCart }) => {
        onCartChange(newCart);

        setUndoInfo(newUndoInfo);
        setShowLoader(false);
        return {
          success: true,
        };
      })
      .catch(({ message }) => {
        setShowLoader(false);
        setCartApiMessage(message);
        return {
          success: false,
        };
      });
  }, []);

  const toggleRewards = useCallback(() => {
    setShowLoader(true);
    const { rewardsAvailable = 0, rewardsApplied = 0 } = { ...cart?.rewardsDTO };
    const transactionType = rewardsApplied > 0 ? 'remove' : 'apply';
    const reward = { type: transactionType, used: rewardsAvailable };

    return toggleRewardToCart(reward, transactionType)
      .then(({ cart: newCart }) => {
        setShowLoader(false);
        onCartChange(newCart);
        setCartApiMessage(
          transactionType === 'apply' ? TEXTS.REWARD_SUCCESS : TEXTS.REMOVE_SUCCESS
        );
        return {
          success: true,
        };
      })
      .catch(({ message }) => {
        setShowLoader(false);
        setCartApiMessage(message);
        return {
          message,
          success: false,
        };
      });
  }, [cart]);

  const onUndoRemoveItem = useCallback(() => {
    if (undoInfo.length === 0) {
      return;
    }

    setShowLoader(true);
    addToCart({ items: undoInfo })
      .catch(() => {
        /** DO NOTHING */
      })
      .then(() => {
        return getCartRefresh({ shouldSendPincode });
      })
      .then(({ cart: newCart }) => {
        onCartChange(newCart);

        setUndoInfo([]);
        setShowLoader(false);
      });
  }, [shouldSendPincode, undoInfo]);

  const onToastHide = useCallback(() => setUndoInfo([]), []);

  const onAddtoCartMessageHide = useCallback(() => setCartApiMessage(''), []);

  const refreshCart = useCallback(() => {
    const sourcePlatform = getDeviceType() === DEVICE_TYPE.DESKTOP ? DEVICE.DESKTOP : DEVICE.MWEB;
    NFA.setSourcePlatform(sourcePlatform);
    setShowLoader(true);

    return getCartRefresh({ shouldSendPincode })
      .then(async (data) => {
        setCart(data.cart);
        setShowLoader(false);
        const error = get(data, 'cart.error', null);
        const pincodeCity = get(data, 'cart.pincodeCity', null);
        if (error) {
          return {
            success: false,
            message: error?.errorMessage,
            pincodeCity: pincodeCity,
          };
        } else {
          return {
            success: true,
            pincodeCity: pincodeCity,
          };
        }
      })
      .catch(() => {
        setShowLoader(false);
      });
  }, [shouldSendPincode]);

  // apply coupons
  const applyCoupon = useCallback((couponCode: string) => {
    setShowLoader(true);
    return applyCouponApi(couponCode)
      .then(({ cart: newCart, message }) => {
        setShowCouponAppliedNudge(true);

        onCartChange(newCart);

        const discount = get(newCart, 'cartTotal.couponDiscount.amount', 0);

        setShowLoader(false);
        return {
          success: true,
          message,
          discount,
        };
      })
      .catch((error) => {
        log({
          errTitle: 'Error in applyCoupon',
          tags: { [TAG_IDENTIFIER.MODULE]: 'COUPON_V2', message: error.message },
        });

        setShowLoader(false);
        return {
          message: error.message,
          success: false,
        };
      });
  }, []);

  // remove coupons
  const removeCoupon = useCallback(() => {
    setShowLoader(true);

    return removeCouponApi()
      .then(({ cart: newCart }) => {
        setShowLoader(false);

        onCartChange(newCart);
        return {
          success: true,
        };
      })
      .catch(({ error }) => {
        log({
          errTitle: 'Error in removeCoupon',
          tags: { [TAG_IDENTIFIER.MODULE]: 'COUPON_V2', message: error?.message },
        });

        setShowLoader(false);
        return {
          message: error?.message,
          success: false,
        };
      });
  }, []);

  const setShippingAddressV2 = useCallback((addressId: string, source: string) => {
    setShowLoader(true);

    return setShippingAddressApiV2(addressId, source)
      .then(({ cart: newCart, success, statusCode }) => {
        setShowLoader(false);
        const error = get(newCart, 'error', null);
        if (error || !success) {
          return {
            success: false,
            message: error?.errorMessage,
            statusCode: statusCode,
          };
        } else {
          setCart(newCart);
          return {
            success: true,
          };
        }
      })
      .catch(({ message }) => {
        setShowLoader(false);
        return {
          success: false,
          message,
        };
      });
  }, []);

  const removeAndApplyCoupon = useCallback((couponCode: string) => {
    setShowLoader(true);

    return applyCouponApi(couponCode)
      .then(({ cart: newCart, message }) => {
        setShowCouponAppliedNudge(true);

        onCartChange(newCart);

        const discount = toXDecimal(get(newCart, 'cartTotal.couponDiscount.amount', 0));

        setShowLoader(false);

        return {
          success: true,
          message,
          discount,
        };
      })
      .catch((error) => {
        log({
          errTitle: 'Error in removeAndApplyCoupon',
          tags: { [TAG_IDENTIFIER.MODULE]: 'COUPON_V2', message: error.message },
        });
        setShowLoader(false);
        return {
          message: error.message,
          success: false,
        };
      });
  }, []);

  const undoApplyCoupon = useCallback(() => {
    removeCoupon();
  }, [removeCoupon]);

  const onCouponToastHide = useCallback(() => {
    setCouponSuccessMessage('');
  }, []);

  const updateCartItems = useCallback(({ items }: { items: ApiItem[] }) => {
    return updateCartItemsApi({ items })
      .then(({ cart: newCart }) => {
        setCart(newCart);

        return {
          success: true,
        };
      })
      .catch(({ message }) => {
        return {
          message,
          success: false,
        };
      });
  }, []);

  const replaceCartItemSize = useCallback(({ items }: { items: ApiSizeItem[] }) => {
    return replaceCartItemSizeApi({ items })
      .then(({ cart: newCart }) => {
        onCartChange(newCart);

        return {
          success: true,
        };
      })
      .catch(({ message }) => {
        return {
          message,
          success: false,
        };
      });
  }, []);

  const setShippingAddress = useCallback(
    (addressId: string) => {
      setShowLoader(true);

      return setShippingAddressApi(addressId)
        .then((res: AddressDataApi) => {
          // will save the new address pincode to NYKAA_pincode and pdp will use this new pincode for serviceable
          const pincode = get(res, 'shippingAddress.pincode', '');
          setPincodeToStorage(pincode);

          return getCartRefresh({ shouldSendPincode });
        })
        .then(({ cart: newCart }) => {
          onCartChange(newCart);

          setShowLoader(false);
          return {
            success: true,
          };
        })
        .catch(({ message }) => {
          setShowLoader(false);
          return {
            success: false,
            message,
          };
        });
    },
    [shouldSendPincode]
  );

  const setGuestShippingAddress = useCallback(
    (address: Omit<AddressDataApi, 'addressId'>) => {
      return setGuestShippingAddressApi(address)
        .then((res: AddressDataApi) => {
          // will save the new address pincode to NYKAA_pincode and pdp will use this new pincode for serviceable
          const pincode = get(res, 'shippingAddress.pincode', '');
          setPincodeToStorage(pincode);

          return getCartRefresh({ shouldSendPincode });
        })
        .then(({ cart: newCart }) => {
          onCartChange(newCart);

          setShowLoader(false);
          return {
            success: true,
          };
        })
        .catch(({ message }) => {
          setShowLoader(false);
          return {
            success: false,
            message,
          };
        });
    },
    [shouldSendPincode]
  );

  const handleRemoveItems = useCallback(
    ({ items, onSuccess, onMessage, onSuccessToastProps }: HandleRemoveItemProps) => {
      const cartItemIdProducts = items.filter((item) => item.cartItemId);
      const nonCartItemProducts = items.filter((item) => !item.cartItemId);

      const productIdDTO = cartItemIdProducts.map((item) => ({
        productId: item.productId,
        cartItemId: item.cartItemId,
      }));

      const productIds = nonCartItemProducts.map((item) => item.productId);

      return deleteCartItem(productIds, productIdDTO)
        .then(({ cart: newCart }) => {
          onSuccess();
          onSuccessToastProps && setCustomToastProps(onSuccessToastProps);

          onCartChange(newCart);

          return {
            success: true,
          };
        })
        .catch(({ message }) => {
          onMessage(message);
          return {
            success: false,
          };
        });
    },
    []
  );

  const setShowRemoveConfirmOOS = useCallback((show: boolean) => setShowRemoveConfirm(show), []);

  const values = useMemo(
    () => ({
      cart,
      isFetching,
      currencySymbol,
      addItemsToCart,
      removeCartItems,
      applyCoupon,
      updateCartItems,
      replaceCartItemSize,
      removeCoupon,
      setShippingAddress,
      setGuestShippingAddress,
      removeAndApplyCoupon,
      showCouponAppliedNudge,
      isPaymentSummaryCollapsed: isCollapsed,
      setShowCouponAppliedNudge,
      itemCount: getCartItemCounts(cart),
      quantity: getCartItemQuantity(cart),
      freeItems: getFreeItems(cart),
      products: getProducts(cart),
      paymentDetails: getPaymentDetails(cart),
      grandTotal: getGrandTotal(cart),
      outOfStockItems: getOOSItems(cart),
      shippingMessage: getShippingMessage(cart),
      shippingCommunicationText: getShippingCommunicationText(cart),
      couponCode: getCouponCode(cart),
      address: getAddress(cart),
      isAddressContinueEnabled: isAddressContinueEnabled(cart),
      isCartContinueEnabled: isCartContinueEnabled(cart),
      cartErrorMessage: getCartErrorMessage(cart),
      cartInfoMessage: getCartInfoMessage(cart),
      addressErrorMessage: getAddressErrorMessage(cart),
      handleRemoveItems: handleRemoveItems,
      handleCartRefresh: handleCartRefresh,
      couponDiscount: getCouponDiscount(cart),
      setPaymentSummaryCollapsed: setIsCollapsed,
      appliedOfferLabelDetails: getCartAppliedOffersDetails(cart),
      rewardsDTO: getRewardsDTO(cart),
      convenienceChargesData: getConvenienceChargesData(cart),
      freeGiftsBannerData: getFreeGiftsBannerData(cart),
      freeSamples: getFreeSamples(cart),
      toggleRewards,
      setShowRemoveConfirmOOS,
      showRemoveConfirmOOS,
      fomoTagDTO: getFomoTag(cart),
      removeOOSFreeSamples,
      setRemoveOOSFreeSamples,
      OOSFreeItems: getOOSFreeItems(cart),
      setShippingAddressV2,
      refreshCart,
    }),
    [
      cart,
      isFetching,
      currencySymbol,
      addItemsToCart,
      removeCartItems,
      applyCoupon,
      updateCartItems,
      replaceCartItemSize,
      removeCoupon,
      setShippingAddress,
      setGuestShippingAddress,
      removeAndApplyCoupon,
      showCouponAppliedNudge,
      isCollapsed,
      handleRemoveItems,
      handleCartRefresh,
      toggleRewards,
      setShowRemoveConfirmOOS,
      showRemoveConfirmOOS,
      removeOOSFreeSamples,
      setShippingAddressV2,
      refreshCart,
    ]
  );

  const reloadCart = () => {
    if (getDeviceType() === 'WEBSITE') {
      hideCart();
      return;
    }
    window.location.href = '/';
  };

  if (errorStatusCode != '') {
    return <SomethingWentWrong onBack={reloadCart} statusCode={errorStatusCode} />;
  }
  return (
    <CartContext.Provider value={values}>
      {children}
      {undoInfo.length > 0 && (
        <Toast
          message="Item(s) removed from cart"
          actionText="Undo"
          onActionClick={onUndoRemoveItem}
          onToastHide={onToastHide}
        />
      )}
      {couponSuccessMessage.length > 0 && (
        <Toast
          message={couponSuccessMessage}
          actionText="Undo"
          onActionClick={undoApplyCoupon}
          onToastHide={onCouponToastHide}
        />
      )}
      {customToastProps.message !== '' && (
        <Toast {...customToastProps} onToastHide={() => setCustomToastProps({ message: '' })} />
      )}
      {cartApiMessage && cartApiMessage.length > 0 && (
        <Toast message={cartApiMessage} onToastHide={onAddtoCartMessageHide} />
      )}
      {showLoader && <NFLoader />}
    </CartContext.Provider>
  );
}

export default memo(CartProvider);
